import "../styles/homepage.css";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import DailyMenuTabs from "../components/DailyMenuTabs";
import AdminDailyMenu from "../components/AdminDailyMenu";

export default function HomeLayout({
  alertText = "",
  contact = { hours: "", phone: "", email: "" },
  aboutText = "",
  dailyMenu = { enabled: true, hours: "11:00–14:30", groups: [] },
  stableMenu = { enabled: true, currentPdf: "" },      // DATA
  stableMenuAdmin = null,

  gallery = { enabled: true, images: [] },
  galleryAdmin = null,

  isAdmin = false,
  onChangeAlertText = () => {},
  onChangeContact = () => {},
  onChangeAboutText = () => {},
  onChangeDailyMenu = () => {},
}) {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const menuPdfUrl =
    stableMenu?.enabled && stableMenu?.currentPdf
      ? `/documents/menu/${stableMenu.currentPdf}`
      : "";

  return (
    <div className="page">
      {/* TOPBAR */}
      <div className="topbar">
        <div className="wrap">
          <div className="topbarRow">
            <a className="brand" href="#home" onClick={scrollTo("#home")}>
              <img className="logo" src="/img/logo.png" alt="Logo" />
              <span>
                Everest Plzeň
                <small>Indická & Nepálská kuchyně</small>
              </span>
            </a>

            <nav className="nav" aria-label="Navigace">
              <a href="#daily" onClick={scrollTo("#daily")}><strong>Denní menu</strong></a>
              <a href="#menu" onClick={scrollTo("#menu")}><strong>Menu</strong></a>
              <a href="#gallery" onClick={scrollTo("#gallery")}><strong>Galerie</strong></a>
            </nav>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section id="home" className="hero">
        <div className="wrap">
          <div className="heroGrid">
            <div className="card heroMain">
              <h1 className="h1">Indická & Nepálská restaurace Everest</h1>
              <p className="subtitle">Autentické chutě, jedinečná atmosféra a poctivá kuchyně.</p>

              {(isAdmin || alertText) && (
                <div className="alert" style={{ marginTop: 16 }}>
                  {isAdmin ? (
                    <>
                      <div className="alertTitle">Provozní hláška</div>
                      <textarea
                        className="adminAlertTextarea"
                        value={alertText}
                        onChange={(e) => onChangeAlertText(e.target.value)}
                        rows={3}
                        placeholder="Např. Dnes zavíráme ve 21:00… (prázdné = nezobrazí se na webu)"
                      />
                      <div className="adminHint">
                        Tip: Když necháš prázdné, na veřejné stránce se hláška nezobrazí.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="alertTitle">Provozní hláška</div>
                      <div className="alertText">{alertText}</div>
                    </>
                  )}
                </div>
              )}

              <div className="heroActions">
                <a className="btn btnStrong" href="#daily" onClick={scrollTo("#daily")}>
                  Denní menu <ExternalLink size={16} />
                </a>
                <a className="btn" href="#menu" onClick={scrollTo("#menu")}>
                  Stálé menu <ExternalLink size={16} />
                </a>
              </div>
            </div>

            <div className="card heroSide">
              <Info icon={<MapPin size={18} />} label="Adresa">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Lochotínská+11+301+00+Plzeň"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Lochotínská 11, 301 00 Plzeň
                </a>
                {isAdmin && (
                  <div className="adminHint" style={{ marginTop: 6 }}>
                    Adresa je pevně daná. V případě stěhování nás prosím kontaktujte.
                  </div>
                )}
              </Info>

              <Info icon={<Clock size={18} />} label="Otevírací doba">
                {isAdmin ? (
                  <textarea
                    className="adminInlineTextarea"
                    value={contact.hours || ""}
                    onChange={(e) => onChangeContact({ hours: e.target.value })}
                    rows={4}
                    placeholder={"Po–Čt 11:00–22:00\nPá–So 11:00–23:00\nNe ZAVŘENO"}
                  />
                ) : (
                  <div className="hoursLines">
                    {(contact.hours || "")
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean)
                      .map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                  </div>
                )}
              </Info>

              <Info icon={<Phone size={18} />} label="Telefon">
                {isAdmin ? (
                  <input
                    className="adminInlineInput"
                    value={contact.phone || ""}
                    onChange={(e) => onChangeContact({ phone: e.target.value })}
                    placeholder="+420 774 048 597"
                  />
                ) : (
                  <a href={`tel:${normalizeTel(contact.phone)}`}>{contact.phone}</a>
                )}
              </Info>

              <Info icon={<Mail size={18} />} label="E-mail">
                {isAdmin ? (
                  <input
                    className="adminInlineInput"
                    value={contact.email || ""}
                    onChange={(e) => onChangeContact({ email: e.target.value })}
                    placeholder="indickaplzen@email.cz"
                  />
                ) : (
                  <a href={`mailto:${sanitizeEmail(contact.email)}`}>{contact.email}</a>
                )}
              </Info>
            </div>
          </div>
        </div>
      </section>

      {/* O NÁS */}
      <section id="onas" className="section">
        <div className="wrap">
          <div className="sectionHead">
            <h2 className="h2">O nás</h2>
          </div>

          <div className="block">
            {isAdmin ? (
              <>
                <textarea
                  className="adminInlineTextarea"
                  value={aboutText || ""}
                  onChange={(e) => onChangeAboutText(e.target.value)}
                  rows={6}
                  placeholder="Text o nás…"
                />
                <div className="adminHint">Použij Enter pro nové řádky.</div>
              </>
            ) : (
              (aboutText || "")
                .split("\n")
                .map((line, i) => <div key={i}>{line}</div>)
            )}
          </div>
        </div>
      </section>

      {/* DENNÍ MENU */}
      <section id="daily" className="section">
        <div className="wrap">
          <div className="sectionHead">
            <h2 className="h2">Denní menu</h2>
            <div className="hint">{dailyMenu?.hours ? `Čas: ${dailyMenu.hours}` : ""}</div>
          </div>

          <div className="block">
            {isAdmin ? (
              <AdminDailyMenu
                value={dailyMenu}
                onChange={(patch) => onChangeDailyMenu(patch)}
              />
            ) : (
              <DailyMenuTabs dailyMenu={dailyMenu} />
            )}
          </div>
        </div>
      </section>

     {/* STÁLÉ MENU – PDF */}
<section id="menu" className="section">
  <div className="wrap">
    <div className="sectionHead">
      <h2 className="h2">Naše stálé menu</h2>
      <div className="hint"></div>
    </div>

    {/* ✅ ADMIN panel – jen když isAdmin */}
    {isAdmin && stableMenuAdmin && (
      <div style={{ marginBottom: 14 }}>
        <div className="card" style={{ padding: 16 }}>
          <div className="sectionHead" style={{ marginBottom: 10 }}>
            <h2 className="h2">Naše stálé menu (PDF)</h2>
            <div className="hint">Upload + výběr aktuálního PDF</div>
          </div>

          <div className="adminRow2">
            <div>
              <div className="adminLabel">Zobrazení</div>
              <select
                className="adminInlineInput"
                value={stableMenuAdmin.enabled ? "on" : "off"}
                onChange={(e) => stableMenuAdmin.setEnabled(e.target.value === "on")}
              >
                <option value="on">Zapnuto</option>
                <option value="off">Vypnuto</option>
              </select>

              <div className="adminLabel" style={{ marginTop: 10 }}>
                Datum pro název souboru
              </div>
              <input
                className="adminInlineInput"
                type="date"
                value={stableMenuAdmin.uploadDate}
                onChange={(e) => stableMenuAdmin.setUploadDate(e.target.value)}
                disabled={stableMenuAdmin.uploadBusy}
              />

              <div className="adminLabel" style={{ marginTop: 10 }}>
                Nahrát nové PDF
              </div>
              <input
                type="file"
                accept="application/pdf"
                disabled={stableMenuAdmin.uploadBusy}
                onChange={(e) => stableMenuAdmin.uploadPdf(e.target.files?.[0])}
              />

              {stableMenuAdmin.uploadMsg && (
                <div className="adminHint">{stableMenuAdmin.uploadMsg}</div>
              )}
            </div>

            <div>
              <div className="adminLabel">Aktuální PDF</div>
              <select
                className="adminInlineInput"
                value={stableMenuAdmin.current || ""}
                onChange={(e) => stableMenuAdmin.setCurrent(e.target.value)}
              >
                <option value="">— vyber PDF —</option>
                {(stableMenuAdmin.files || []).map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label || f.name}
                  </option>
                ))}
              </select>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  className="adminSaveBtn"
                  onClick={stableMenuAdmin.refresh}
                  disabled={stableMenuAdmin.uploadBusy}
                  type="button"
                >
                  Obnovit seznam
                </button>

                <button
                  className="adminDangerBtn"
                  onClick={stableMenuAdmin.deleteCurrent}
                  disabled={!stableMenuAdmin.current || stableMenuAdmin.uploadBusy}
                  type="button"
                >
                  Smazat vybrané PDF
                </button>
              </div>

              {stableMenuAdmin.current && (
                <div className="adminHint" style={{ marginTop: 10 }}>
                  Preview:{" "}
                  <a
                    href={`/documents/menu/${stableMenuAdmin.current}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    otevřít PDF
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ✅ Veřejné zobrazení (a admin ho vidí taky) */}
    <div className="block">
      {!menuPdfUrl ? (
        <div className="placeholder">Momentálně není vybrané žádné PDF menu.</div>
      ) : (
        <>
          <div className="menuPdfDesktop">
            <iframe
              src={menuPdfUrl}
              title="Stálé menu PDF"
              width="100%"
              height="1150"
              style={{ border: "none", display: "block" }}
            />
          </div>

          <div className="menuPdfMobile">
            <a className="btn btnStrong" href={menuPdfUrl} target="_blank" rel="noreferrer">
              Otevřít PDF menu <ExternalLink size={16} />
            </a>
          </div>
        </>
      )}
    </div>
  </div>
</section>


      {/* GALERIE placeholder */}
      {/* GALERIE */}
<section id="gallery" className="section">
  <div className="wrap">
    <div className="sectionHead">
      <h2 className="h2">Fotogalerie</h2>
      <div className="hint">{gallery?.enabled ? "" : "Vypnuto"}</div>
    </div>

    <div className="block">
      {/* ADMIN panel */}
      {isAdmin && galleryAdmin && (
        <div style={{ marginBottom: 14 }}>
          <div className="card" style={{ padding: 16 }}>
            <div className="sectionHead" style={{ marginBottom: 10 }}>
              <h2 className="h2">Fotogalerie (admin)</h2>
              <div className="hint">Upload + mazání</div>
            </div>

            <div className="adminRow2">
              <div>
                <div className="adminLabel">Zobrazení</div>
                <select
                  className="adminInlineInput"
                  value={galleryAdmin.enabled ? "on" : "off"}
                  onChange={(e) => galleryAdmin.setEnabled(e.target.value === "on")}
                  disabled={galleryAdmin.uploadBusy}
                >
                  <option value="on">Zapnuto</option>
                  <option value="off">Vypnuto</option>
                </select>

                <div className="adminLabel" style={{ marginTop: 10 }}>
                  Nahrát fotku
                </div>
                <input
                  type="file"
                  accept="image/*"
                  disabled={galleryAdmin.uploadBusy}
                  onChange={(e) => galleryAdmin.uploadImage?.(e.target.files?.[0] || null, "")}
                />

                {galleryAdmin.uploadMsg && (
                  <div className="adminHint" style={{ marginTop: 8 }}>
                    {galleryAdmin.uploadMsg}
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                  <button
                    className="adminSaveBtn"
                    type="button"
                    onClick={galleryAdmin.refresh}
                    disabled={galleryAdmin.uploadBusy}
                  >
                    Obnovit seznam
                  </button>
                </div>
              </div>

              <div>
                <div className="adminLabel">Seznam fotek</div>

                {Array.isArray(galleryAdmin.images) && galleryAdmin.images.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {galleryAdmin.images.map((img) => {
                      const name = img?.name || "";
                      const url =
                        img?.url || (name ? `/gallery/${name}` : "");
                      return (
                        <div
                          key={name}
                          className="adminLine"
                          style={{ alignItems: "center" }}
                        >
                          <div className="adminLineLeft" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <div
                              style={{
                                width: 54,
                                height: 40,
                                borderRadius: 10,
                                overflow: "hidden",
                                border: "1px solid var(--line)",
                                background: "rgba(0,0,0,.18)",
                                flex: "0 0 auto",
                              }}
                            >
                              {url ? (
                                <img
                                  src={url}
                                  alt={img?.label || ""}
                                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                  loading="lazy"
                                />
                              ) : null}
                            </div>

                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {img?.label || name}
                              </div>
                              <div style={{ fontSize: 12, opacity: 0.7 }}>{name}</div>
                            </div>
                          </div>

                          <div className="adminLineBtns">
                            {url ? (
                              <a className="adminIconBtn" href={url} target="_blank" rel="noreferrer" title="Otevřít">
                                ↗
                              </a>
                            ) : null}
                            <button
                              className="adminIconBtn danger"
                              type="button"
                              onClick={() => galleryAdmin.deleteImage(name)}
                              disabled={galleryAdmin.uploadBusy}
                              title="Smazat"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="placeholder">Zatím žádné fotky.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
       

      {/* PUBLIC zobrazení */}
      {!gallery?.enabled ? (
        <div className="placeholder">Fotogalerie je momentálně vypnutá.</div>
      ) : !Array.isArray(gallery?.images) || gallery.images.length === 0 ? (
        
        <div className="placeholder">Zatím bez fotek.</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 10,
          }}
        >
          {gallery.images.map((img) => {
            const name = img?.name || "";
            const url = img?.url || (name ? `/gallery/${name}` : "");
            if (!url) return null;

            return (
              <a
                key={name}
                href={url}
                target="_blank"
                rel="noreferrer"
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  border: "1px solid var(--line)",
                  background: "rgba(0,0,0,.18)",
                  display: "block",
                  aspectRatio: "4 / 3",
                }}
                title={img?.label || ""}
              >
                <img
                  src={url}
                  alt={img?.label || ""}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  loading="lazy"
                />
              </a>
            );
          })}
        </div>
      )}
    </div>
  </div>
</section>


      {/* KONTAKT placeholder */}
      <section id="contact" className="section">
        <div className="wrap">
          <div className="sectionHead">
            <h2 className="h2"></h2>
            <div className="hint"></div>
          </div>
          <div className="block">
  <div
    className="mapWrap"
    style={{
      height: 320,
      borderRadius: 16,
      overflow: "hidden",
      border: "1px solid var(--line)",
      background: "rgba(0,0,0,.18)",
    }}
  >
    <iframe
      title="Mapa – Restaurace Everest Plzeň"
      src="https://www.google.com/maps?q=Lochot%C3%ADnsk%C3%A1%2011%2C%20301%2000%20Plze%C5%88&output=embed"
      width="100%"
      height="100%"
      style={{ border: 0, display: "block" }}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      allowFullScreen
    />
  </div>

  {/* Volitelné: tlačítko pro otevření v mapách */}
  <div style={{ marginTop: 12 }}>
    <a
      className="btn"
      href="https://www.google.com/maps/search/?api=1&query=Lochot%C3%ADnsk%C3%A1%2011%2C%20301%2000%20Plze%C5%88"
      target="_blank"
      rel="noreferrer"
    >
      Otevřít navigaci
    </a>
  </div>
</div>

        </div>
      </section>

      <div className="wrap footer">
  © {new Date().getFullYear()} Restaurace Everest. All rights reserved. <br />
  Provozovatel: Bishnu Bahadur Rayamajhi, IČO: 87306654 <br />
  {/* případně doplnit: Adresa, Email, Telefon */}
  Created by NuWiDu.
</div>

    </div>
  );
}

const Info = ({ icon, label, children }) => (
  <div className="infoRow">
    <div className="icon">{icon}</div>
    <div>
      <b>{label}</b>
      <div>{children}</div>
    </div>
  </div>
);

function normalizeTel(v = "") {
  return String(v).replace(/[^\d+]/g, "");
}
function sanitizeEmail(v = "") {
  return String(v).trim().replace(/\s+/g, "");
}
